import React from 'react';
import { Cloud, Shield, Database, Lock } from 'lucide-react';
import { useLanguage } from '../../components/LanguageContext';

export function CloudSecurity() {
  const { language } = useLanguage();
  
  const content = {
    hero: {
      title: {
        fr: 'Sécurité Cloud',
        en: 'Cloud Security'
      },
      subtitle: {
        fr: 'Protection complète de vos environnements cloud',
        en: 'Comprehensive protection for your cloud environments'
      }
    },
    features: [
      {
        icon: <Shield className="w-8 h-8 text-blue-500" />,
        title: {
          fr: 'Protection Continue',
          en: 'Continuous Protection'
        },
        description: {
          fr: 'Surveillance et protection 24/7 de vos ressources cloud',
          en: '24/7 monitoring and protection of your cloud resources'
        }
      },
      {
        icon: <Database className="w-8 h-8 text-blue-500" />,
        title: {
          fr: 'Sécurité des Données',
          en: 'Data Security'
        },
        description: {
          fr: 'Chiffrement et protection des données sensibles',
          en: 'Encryption and protection of sensitive data'
        }
      },
      {
        icon: <Lock className="w-8 h-8 text-blue-500" />,
        title: {
          fr: 'Conformité',
          en: 'Compliance'
        },
        description: {
          fr: 'Respect des normes de sécurité et de conformité',
          en: 'Adherence to security and compliance standards'
        }
      }
    ],
    services: {
      title: {
        fr: 'Services de Sécurité Cloud',
        en: 'Cloud Security Services'
      },
      items: [
        {
          title: {
            fr: 'Configuration Sécurisée',
            en: 'Secure Configuration'
          },
          description: {
            fr: 'Audit et correction des configurations de sécurité cloud',
            en: 'Audit and remediation of cloud security configurations'
          }
        },
        {
          title: {
            fr: 'Protection des Workloads',
            en: 'Workload Protection'
          },
          description: {
            fr: 'Sécurisation des applications et services cloud',
            en: 'Security for cloud applications and services'
          }
        },
        {
          title: {
            fr: 'Gestion des Accès',
            en: 'Access Management'
          },
          description: {
            fr: 'Contrôle granulaire des accès et des identités',
            en: 'Granular control of access and identities'
          }
        }
      ]
    }
  };

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Hero Section */}
      <div className="hero-pattern py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <Cloud className="w-16 h-16 text-blue-500 mx-auto mb-6" />
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              {content.hero.title[language]}
            </h1>
            <p className="text-xl text-gray-300">
              {content.hero.subtitle[language]}
            </p>
          </div>
        </div>
      </div>

      {/* Features Grid */}
      <div className="container mx-auto px-6 py-20">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {content.features.map((feature, index) => (
            <div key={index} className="bg-gray-800/50 backdrop-blur-sm p-8 rounded-lg border border-gray-700/50 hover:border-blue-500/50 transition-colors duration-300">
              <div className="mb-4">{feature.icon}</div>
              <h3 className="text-xl font-bold text-white mb-3">
                {feature.title[language]}
              </h3>
              <p className="text-gray-300">
                {feature.description[language]}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Services Section */}
      <div className="bg-gray-800/50 py-20">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl font-bold text-white text-center mb-12">
            {content.services.title[language]}
          </h2>
          <div className="max-w-4xl mx-auto space-y-8">
            {content.services.items.map((item, index) => (
              <div key={index} className="flex items-start space-x-4 bg-gray-900/50 p-6 rounded-lg">
                <div className="flex-shrink-0 bg-blue-500/10 p-3 rounded-lg">
                  <Cloud className="w-6 h-6 text-blue-500" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-white mb-2">
                    {item.title[language]}
                  </h3>
                  <p className="text-gray-300">
                    {item.description[language]}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Cloud Security Visualization */}
      <div className="container mx-auto px-6 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="bg-gray-800/50 rounded-lg overflow-hidden shadow-2xl">
            <img 
              src="https://images.unsplash.com/photo-1544197150-b99a580bb7a8?auto=format&fit=crop&q=80" 
              alt="Cloud Infrastructure"
              className="w-full h-64 object-cover"
            />
            <div className="p-6">
              <h3 className="text-xl font-bold text-white mb-2">
                {language === 'fr' ? 'Infrastructure Cloud' : 'Cloud Infrastructure'}
              </h3>
              <p className="text-gray-300">
                {language === 'fr' 
                  ? 'Protection complète de vos ressources cloud'
                  : 'Complete protection of your cloud resources'}
              </p>
            </div>
          </div>
          <div className="bg-gray-800/50 rounded-lg overflow-hidden shadow-2xl">
            <img 
              src="https://images.unsplash.com/photo-1558494949-ef010cbdcc31?auto=format&fit=crop&q=80" 
              alt="Security Operations"
              className="w-full h-64 object-cover"
            />
            <div className="p-6">
              <h3 className="text-xl font-bold text-white mb-2">
                {language === 'fr' ? 'Centre des Opérations' : 'Operations Center'}
              </h3>
              <p className="text-gray-300">
                {language === 'fr' 
                  ? 'Surveillance 24/7 de votre environnement cloud'
                  : '24/7 monitoring of your cloud environment'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="container mx-auto px-6 py-20">
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-12 text-center relative overflow-hidden">
          <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&q=80')] opacity-10 bg-cover bg-center"></div>
          <div className="relative z-10">
            <h2 className="text-3xl font-bold text-white mb-4">
              {language === 'fr' ? 'Sécurisez Votre Infrastructure Cloud' : 'Secure Your Cloud Infrastructure'}
            </h2>
            <p className="text-xl text-blue-100 mb-8">
              {language === 'fr' 
                ? 'Découvrez comment nous pouvons protéger vos ressources cloud' 
                : 'Discover how we can protect your cloud resources'}
            </p>
            <button className="bg-white text-blue-600 px-8 py-3 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-all duration-300 transform hover:scale-105">
              {language === 'fr' ? 'Commencer la Protection' : 'Start Protection'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}